// src/pages/PainelAgendamentos/Agendamentos.jsx
import { useEffect, useState } from "react";
import { ChevronRight, LayoutDashboard, CalendarPlus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Rodape from "../rodape/Rodape";
import FormAgendamento from "./FormAgendamento";
import StatusAgendamentoCards from "./StatusAgendamentoCards";
import TabelaAgendamento from "./Tabela/TabelaAgendamento";

const API_URL = process.env.REACT_APP_API_URL;

export default function Agendamentos() {
  const [modoFormulario, setModoFormulario] = useState(false);
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtro global vindo dos cards ("" = Todos)
  const [statusAtual, setStatusAtual] = useState("");

  const handleNovoAgendamento = () => {
    setModoFormulario(true);
  };

  // Normaliza string (remove acento + minúscula)
  const normalizar = (str) =>
    str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";

  useEffect(() => {
    async function fetchAgendamentos() {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/agendamento/listar.php`);
        const json = await res.json();

        if (json.status && Array.isArray(json.dados)) {
          const dadosTratados = json.dados.map((item) => ({
            id: item.id,
            data_abertura: item.data,
            hora_abertura: item.hora,
            finalizado_em: item.dataFinalizado || "0000-00-00",
            empresa: item.empresa,
            colaborador: item.responsavelEmpresa,
            tipo_servico: item.servico,
            tecnico_responsavel: item.tecnico,
            status: item.status,
          }));
          setDados(dadosTratados);
        } else {
          setDados([]);
        }
      } catch (err) {
        console.error("Erro ao buscar agendamentos:", err);
        setDados([]);
      } finally {
        setLoading(false);
      }
    }

    fetchAgendamentos();
  }, []);

  // Calcula totais reais (usando normalização pra evitar problemas de acento/maiusc)
  const statusCounts = {
    todos: dados.length,
    pendente: 0,
    concluido: 0, // 🔹 ajustado
    cancelado: 0,
    reagendado: 0,
  };

  dados.forEach((d) => {
    const s = normalizar(d.status || "");
    if (s.includes("pendente")) statusCounts.pendente++;
    else if (s.includes("finaliz") || s.includes("conclu")) statusCounts.concluido++; // 🔹 ajustado
    else if (s.includes("cancel")) statusCounts.cancelado++;
    else if (s.includes("reagend")) statusCounts.reagendado++;
  });

  return (
    <div className="h-full w-full relative overflow-hidden flex flex-col">
      <AnimatePresence mode="wait">
        {!modoFormulario ? (
          <motion.div
            key="principal"
            initial={{ x: 0, opacity: 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "-100%", opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex-1 w-full h-full p-6 pt-4 flex flex-col"
          >
            {/* Breadcrumb */}
            <nav
              className="flex items-center space-x-3 text-sm mb-4"
              aria-label="Breadcrumb"
            >
              <LayoutDashboard size={16} className="text-slate-600" />
              <span className="cursor-pointer font-medium text-slate-700 hover:underline">
                Agendamentos
              </span>
              <ChevronRight size={12} className="text-slate-500" />
              <span className="text-slate-700 font-medium">
                Visitas Técnicas
              </span>
            </nav>

            {/* Quadro principal */}
            <section className="bg-white rounded-md shadow-sm border border-[#4d4d4d] overflow-hidden flex-1 flex flex-col">
              <div className="w-full bg-[#2b2b2b] px-4 py-2 flex items-center justify-between">
                <button
                  onClick={handleNovoAgendamento}
                  aria-label="Abrir novo agendamento"
                  className="flex items-center gap-2 px-4 py-2 text-white font-medium rounded transition-transform duration-200 hover:scale-105 bg-[#f95000]"
                >
                  <CalendarPlus size={18} />
                  <span className="hidden sm:inline">Novo Agendamento</span>
                </button>
              </div>

              {/* Cards — recebe os counts calculados */}
              <StatusAgendamentoCards
                statusCounts={statusCounts}
                onSelectStatus={(s) => setStatusAtual(s)}
                activeKey={statusAtual} // passa o ativo para destacar
              />

              {/* Tabela — recebe os dados e o filtro de status */}
              <div className="flex-1 p-4">
                <TabelaAgendamento
                  dados={dados}
                  statusFiltro={statusAtual}
                  loading={loading}
                />
              </div>
            </section>

            <div className="mt-4">
              <Rodape />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="formulario"
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full h-full p-6 flex flex-col"
          >
            <FormAgendamento
              onVoltar={() => setModoFormulario(false)}
              onSalvo={() => {
                setModoFormulario(false);
                setStatusAtual("Concluído"); // 🔹 ajustado
              }}
            />
            <div className="mt-4">
              <Rodape />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
